import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MoonStar, Clock3, ShieldCheck, HeartHandshake } from "lucide-react";
import { LineReveal, Reveal, Marquee, FloatingOrbs, Chapter, ParallaxImage } from "../components/motion";
import CTABand from "../components/CTABand";
import PackageCard from "../components/PackageCard";
import TestimonialMarquee from "../components/TestimonialMarquee";
import { api } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1542387960-f8197d82db42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxA3MDB8MHx1YXJjaHwxfHxwZWFjZWZ1bCUyMHNsZWVwaW5nJTIwYmFieSUyMG51cnNlcnklMjB3YXJtJTIwbGlnaHR8ZW58MHx8fHwxNzg3ODU2NTQwfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  useSEO({
    title: "The Nanny Sleep Co. — Gentle Baby & Toddler Sleep Consulting",
    description: "Gentle, evidence-informed sleep support for exhausted parents. Tailored sleep plans, no cry-it-out, from newborn to five years. Start with a free 2-minute assessment.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "The Nanny Sleep Co.",
      description: "Gentle, evidence-informed infant and child sleep consulting.",
      email: "hello@thenannysleepco.com.au",
      founder: { "@type": "Person", name: "Tamika", jobTitle: "Certified infant & child sleep consultant" },
      areaServed: "Australia (online)",
    },
  });
  usePageView("/");

  useEffect(() => {
    api.packages().then(setPackages).catch(() => {});
    api.testimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <FloatingOrbs />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-24 sm:pb-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Reveal delay={0.05}>
                <p className="overline-label mb-6">Gentle · Evidence-informed · Never judgement</p>
              </Reveal>
              <LineReveal
                data-testid="hero-headline"
                className="font-serif font-light text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.08] tracking-tight text-ink"
                lines={["More sleep.", "Less stress.", "For you and your", "little one."]}
              />
              <Reveal delay={0.75}>
                <p className="mt-7 text-lg text-ink-soft leading-relaxed max-w-lg">
                  You're doing an incredible job on very little sleep. I'm Tamika — a certified infant & child sleep consultant and former professional nanny — and I help families find their way back to rest, gently.
                </p>
              </Reveal>
              <Reveal delay={0.9}>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <Link to="/sleep-assessment" data-testid="hero-assessment-cta" className="btn-primary">
                    Take the 2-minute assessment <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/book-a-call" data-testid="hero-book-cta" className="btn-secondary">
                    Book a free 15-minute suitability call
                  </Link>
                </div>
                <p className="mt-5 text-xs text-ink-muted">Five gentle questions. One clear next step. No pressure, ever.</p>
              </Reveal>
            </div>
            <Reveal delay={0.4} className="relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-terra-light rounded-[2.5rem] rotate-2" aria-hidden="true" />
                <div className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(44,40,37,0.14)] aspect-[4/5] max-w-md mx-auto">
                  <img src={HERO_IMG} alt="A baby sleeping peacefully in a warm, softly lit nursery" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-4 sm:-left-8 card-soft px-5 py-4 flex items-center gap-3">
                  <MoonStar className="w-5 h-5 text-terra" strokeWidth={1.8} />
                  <p className="text-sm font-semibold text-ink">Newborn to five years</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={["Gentle methods only", "Tailored to your child", "Certified sleep consultant", "6+ years as a professional nanny", "Support while you implement", "Zero judgement"]} />

      {/* MANIFESTO CHAPTERS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <p className="overline-label mb-4">If it's 3am and you're reading this</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-medium text-ink leading-tight mb-14">
              You don't need more conflicting advice. You need one clear next step.
            </h2>
          </Reveal>
          <div className="space-y-16">
            <Chapter number="01" title="First, we answer the question you're actually asking">
              <p>Not a generic article about sleep. The real one — <em>why is my 6-month-old waking every hour, and what do I do tonight?</em> Every page here is built around real questions from real consultations.</p>
            </Chapter>
            <Chapter number="02" title="Then, a calm and credible approach you can trust">
              <p>Gentle and responsive, always. Evidence-informed, never one-size-fits-all, and honest about what sleep support can and can't promise. If something sounds medical, I'll point you to your GP first.</p>
            </Chapter>
            <Chapter number="03" title="Finally, a next step that fits your family">
              <p>Sometimes that's a self-paced guide. Sometimes it's a focused call. Sometimes it's a full plan with a month of support. The free 2-minute assessment tells you which — and books take under a minute when you're ready.</p>
            </Chapter>
          </div>
          <Reveal delay={0.1} className="mt-14">
            <Link to="/sleep-assessment" data-testid="manifesto-assessment-cta" className="btn-primary">
              Get your tailored next step <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY TRUST */}
      <section className="py-20 bg-warm/70 border-y border-ink/[0.05]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: HeartHandshake, title: "Gentle, always", body: "No cry-it-out. Responsive methods that protect your bond and feel sustainable — not just survivable." },
              { icon: ShieldCheck, title: "Certified & experienced", body: "A certified infant & child sleep consultant with 6+ years as a professional nanny, across every age and stage." },
              { icon: Clock3, title: "Clear about delivery", body: "Defined call lengths, defined support windows, honest pricing. You'll always know exactly what you're getting." },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.1}>
                <div className="card-soft p-8 h-full">
                  <f.icon className="w-7 h-7 text-terra mb-5" strokeWidth={1.6} />
                  <h3 className="font-serif text-xl font-medium text-ink mb-2.5">{f.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL MARQUEE */}
      <section className="py-24 sm:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <Reveal className="max-w-2xl">
            <p className="overline-label mb-4">Parent stories</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight">
              From families who were exactly where you are
            </h2>
          </Reveal>
        </div>
        <TestimonialMarquee testimonials={testimonials} />
        <Reveal className="mt-10 text-center">
          <Link to="/parent-stories" data-testid="home-stories-link" className="inline-flex items-center gap-2 text-terra font-semibold text-sm hover:gap-3 transition-all">
            Read more parent stories <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* SUPPORT PREVIEW */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="overline-label mb-4">Support options</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight">
              A gentle, practical plan tailored to your child and family — with defined support to help you implement it.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, i) => (
              <PackageCard key={pkg.id} pkg={pkg} index={i} highlight={pkg.tag === "Most chosen"} />
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/support-options" data-testid="home-compare-link" className="inline-flex items-center gap-2 text-terra font-semibold text-sm hover:gap-3 transition-all">
              Compare the options in detail <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER STRIP */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <ParallaxImage
                src="https://images.squarespace-cdn.com/content/v1/68b7e04ed5afe32710b42d83/8cef08b2-5d57-44a4-bb44-244be6da555c/IMG_5703.jpg"
                alt="Tamika, founder of The Nanny Sleep Co."
                className="rounded-[2rem] aspect-[4/3] shadow-[0_20px_50px_rgba(44,40,37,0.12)]"
              />
            </Reveal>
            <Reveal delay={0.15}>
              <p className="overline-label mb-4">Meet your consultant</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight mb-6">Hi, I'm Tamika.</h2>
              <p className="text-ink-soft leading-relaxed mb-4">
                I've spent over six years as a professional nanny, caring for children of all ages and supporting families through some of their most important milestones — and their most exhausting nights.
              </p>
              <p className="text-ink-soft leading-relaxed mb-8">
                I know how overwhelming it feels when your little one isn't sleeping. My role is to take the guesswork out of settling and routines, so you feel confident, supported and never alone in it.
              </p>
              <Link to="/about" data-testid="home-about-link" className="btn-secondary">Read my story</Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
